
export interface SalesAttributionDateRange {
  startDate: string;
  endDate: string;
}

export interface SalesAttributionMetric {
  value: number;
  formattedValue: string;
}

export const EMPTY_SALES_ATTRIBUTION_METRIC: SalesAttributionMetric = {
  value: 0,
  formattedValue: "—",
};

export type SalesAttributionAdPlatform = "google_ads" | "meta_ads";

export const SALES_ATTRIBUTION_ALL_FILTER_VALUE = "all";

export interface SalesAttributionFilterState {
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  platform: string;
  campaign: string;
}

export const DEFAULT_SALES_ATTRIBUTION_FILTERS: SalesAttributionFilterState = {
  utmSource: "",
  utmMedium: "",
  utmCampaign: "",
  platform: "",
  campaign: "",
};

export interface SalesAttributionOption {
  value: string;
  label: string;
}

export interface SalesAttributionPlatformOption extends SalesAttributionOption {
  available?: boolean;
}

export interface SalesAttributionCampaignOption extends SalesAttributionOption {
  platforms: string[];
}

export interface SalesAttributionFilterOptions {
  utmSources: SalesAttributionOption[];
  utmMediums: SalesAttributionOption[];
  utmCampaigns: SalesAttributionOption[];
  platforms: SalesAttributionPlatformOption[];
  campaigns: SalesAttributionCampaignOption[];
}

export const EMPTY_SALES_ATTRIBUTION_FILTER_OPTIONS: SalesAttributionFilterOptions = {
  utmSources: [],
  utmMediums: [],
  utmCampaigns: [],
  platforms: [],
  campaigns: [],
};

export type SalesAttributionKpiKey =
  | "shopifyRevenue"
  | "shopifyUsers"
  | "ga4Revenue"
  | "ga4Users"
  | "googleRevenue"
  | "googleSpend"
  | "metaRevenue"
  | "metaSpend"
  | "untrackedRevenue"
  | "untrackedShare"
  | "untrackedOrders";

export interface SalesAttributionKpiEntry extends SalesAttributionMetric {
  previous: SalesAttributionMetric;
  percentChange: number | null;
  percentChangeFormattedValue: string | null;
  available: boolean;
}

export type SalesAttributionKpis = Record<SalesAttributionKpiKey, SalesAttributionKpiEntry>;

export type SalesAttributionSeriesKey =
  | "shopifyOrders"
  | "ga4Orders"
  | "googleOrders"
  | "metaOrders"
  | "googleOrdersShopify"
  | "metaOrdersShopify"
  | "googleOrdersGA4"
  | "metaOrdersGA4"
  | "shopifyRevenue"
  | "ga4Revenue"
  | "googleRevenue"
  | "metaRevenue"
  | "googleSpend"
  | "metaSpend";

export type SalesAttributionTrendPoint = {
  date: string;
  dateLabel: string;
  formatted: Record<SalesAttributionSeriesKey, string>;
} & Record<SalesAttributionSeriesKey, number>;

export interface SalesAttributionChannelComparisonRow {
  channelId: string;
  channelName: string;
  shopifyRevenue: SalesAttributionMetric;
  ga4Revenue: SalesAttributionMetric;
}

export interface SalesAttributionChannelRow {
  channelId: string;
  channelName: string;
  spend: SalesAttributionMetric;
  platformRevenue: SalesAttributionMetric;
  attributedRevenue: SalesAttributionMetric;
  roas: SalesAttributionMetric;
  orders: SalesAttributionMetric;
  impressions: SalesAttributionMetric;
  clicks: SalesAttributionMetric;
  ctr: SalesAttributionMetric;
}

export interface SalesAttributionOverlapSet {
  googleClaims: SalesAttributionMetric;
  metaClaims: SalesAttributionMetric;
  googleOnly: SalesAttributionMetric;
  metaOnly: SalesAttributionMetric;
  claimedByBoth: SalesAttributionMetric;
  overlapPercent: SalesAttributionMetric;
}

export interface SalesAttributionOverlapStats {
  orders: SalesAttributionOverlapSet;
  revenue: SalesAttributionOverlapSet;
}

/** Which tenant sources are connected at all. */
export interface SalesAttributionAvailability {
  ga4: boolean;
  googleAds: boolean;
  metaAds: boolean;
}

export interface SalesAttributionView {
  kpis: SalesAttributionKpis;
  availability: SalesAttributionAvailability;
  trend: SalesAttributionTrendPoint[];
  channelComparison: SalesAttributionChannelComparisonRow[];
  channelRows: SalesAttributionChannelRow[];
  scorecardTotal: SalesAttributionChannelRow | null;
  overlap: SalesAttributionOverlapStats;
  filterOptions: SalesAttributionFilterOptions;
  warnings: string[];
  notes: string[];
}
